'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href?: string;
  icon?: string;
  children?: { label: string; href: string }[];
}

// Main travel navigation matching worldwaytravelsgroup.com structure
const TRAVEL_NAV: NavItem[] = [
  {
    label: 'Flights',
    icon: 'PaperAirplaneIcon',
    children: [
      { label: 'Flight Search', href: '/flights' },
      { label: 'Pre-Purchased', href: '/pre-purchased-flights' },
      { label: 'FD Flights', href: '/fd-flights' },
    ],
  },
  {
    label: 'Hotels',
    href: '/hotels',
    icon: 'BuildingOffice2Icon',
  },
  {
    label: 'Voyages',
    icon: 'GlobeAltIcon',
    children: [
      { label: 'Luxury Cruises', href: '/cruises' },
      { label: 'Expedition Cruises', href: '/expedition-cruises' },
      { label: 'River Cruises', href: '/river-cruises' },
      { label: 'World Cruises', href: '/world-cruises' },
      { label: 'Luxury Rail', href: '/rail' },
      { label: 'Yacht Charters', href: '/yachts' },
    ],
  },
  {
    label: 'Journeys',
    icon: 'MapIcon',
    children: [
      { label: 'Small Group', href: '/small-group' },
      { label: 'Tailor-Made', href: '/tailor-made' },
      { label: 'Safari', href: '/safari' },
      { label: 'Polar Expeditions', href: '/polar-expeditions' },
      { label: 'Cultural & Heritage', href: '/cultural' },
      { label: 'Honeymoon', href: '/honeymoon' },
      { label: 'Wellness', href: '/wellness' },
      { label: 'Family', href: '/family' },
      { label: 'Villas & Estates', href: '/villas' },
    ],
  },
  {
    label: 'Experiences',
    icon: 'StarIcon',
    children: [
      { label: 'Tours', href: '/tours' },
      { label: 'Tour Availability', href: '/tour-availability-platform' },
      { label: 'Browse Collections', href: '/tours/browse' },
      { label: 'Activities', href: '/activities' },
      { label: 'Transfers', href: '/transfers' },
      { label: 'Coach & Bus', href: '/buses' },
      { label: 'Trip Builder', href: '/trip-builder' },
      { label: 'Travel Insurance', href: '/insurance' },
      { label: 'Visa Services', href: '/visa' },
    ],
  },
  {
    label: 'Private Aviation',
    icon: 'PaperAirplaneIcon',
    children: [
      { label: 'Private Jets', href: '/private-jets' },
      { label: 'Empty Legs', href: '/private-aviation/empty-legs' },
    ],
  },
  {
    label: 'Concierge',
    href: '/ai-concierge',
    icon: 'SparklesIcon',
  },
  {
    label: 'Membership',
    href: '/membership',
    icon: 'UserCircleIcon',
  },
];

// Platform/enterprise nav links
const PLATFORM_NAV: NavItem[] = [
  { label: 'Customer Portal', href: '/customer-portal', icon: 'UserCircleIcon' },
  { label: 'My Bookings', href: '/my-bookings', icon: 'BookmarkIcon' },
  { label: 'Trip Builder', href: '/trip-builder', icon: 'MapIcon' },
  { label: 'Package Builder', href: '/dynamic-packages', icon: 'PuzzlePieceIcon' },
  { label: 'Support', href: '/customer-support', icon: 'LifebuoyIcon' },
  { label: 'Enterprise CRM', href: '/enterprise-crm', icon: 'UsersIcon' },
  { label: 'CRM Report', href: '/crm-report', icon: 'DocumentChartBarIcon' },
  { label: 'Product Catalog (PIM)', href: '/product-management-platform', icon: 'CubeIcon' },
  { label: 'Product Report', href: '/product-management-report', icon: 'DocumentChartBarIcon' },
  { label: 'UP17 Integration Report', href: '/up17-integration-report', icon: 'ServerStackIcon' },
  { label: 'Enterprise Admin', href: '/enterprise-admin-platform', icon: 'ChartBarIcon' },
  { label: 'AI Travel Intelligence', href: '/ai-travel-intelligence', icon: 'SparklesIcon' },
  { label: 'AI Business Intelligence', href: '/ai-business-intelligence', icon: 'ChartBarIcon' },
  { label: 'Gemini AI Report', href: '/gemini-ai-report', icon: 'DocumentChartBarIcon' },
  { label: 'Orchestration Center', href: '/travel-orchestration-center', icon: 'CpuChipIcon' },
  { label: 'Orchestration Report', href: '/travel-orchestration-report', icon: 'DocumentChartBarIcon' },
  { label: 'Supplier Ops Center', href: '/supplier-operations-center', icon: 'BuildingStorefrontIcon' },
  { label: 'Enterprise Alerts', href: '/enterprise-alerts', icon: 'BellAlertIcon' },
  { label: 'Supplier Testing', href: '/supplier-testing', icon: 'BeakerIcon' },
  { label: 'Ops & Reliability Report', href: '/enterprise-operations-report', icon: 'DocumentChartBarIcon' },
  { label: 'Viator Report', href: '/viator-integration-report', icon: 'MapPinIcon' },
  { label: 'G Adventures Report', href: '/gadventures-integration-report', icon: 'GlobeAltIcon' },
  { label: 'G Adventures Activation', href: '/gadventures-activation-report', icon: 'GlobeAltIcon' },
  { label: 'Booking Ops', href: '/booking-operations', icon: 'CalendarDaysIcon' },
  { label: 'Settlement & Revenue', href: '/settlement-revenue', icon: 'BanknotesIcon' },
  { label: 'Admin Support', href: '/admin-support', icon: 'TicketIcon' },
  { label: 'Merchant Portal', href: '/merchant-portal', icon: 'BuildingStorefrontIcon' },
  { label: 'Supplier Portal', href: '/supplier-portal', icon: 'BuildingOfficeIcon' },
  { label: 'Supplier Support', href: '/supplier-support', icon: 'WrenchScrewdriverIcon' },
  { label: 'AI Concierge', href: '/ai-concierge', icon: 'SparklesIcon' },
  { label: 'Visa Services', href: '/visa', icon: 'DocumentTextIcon' },
  { label: 'Insurance', href: '/insurance', icon: 'ShieldCheckIcon' },
];

function TravelDropdown({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group relative inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground transition-colors duration-300 hover:text-primary"
      >
        {item.label}
        <svg
          width="7"
          height="7"
          viewBox="0 0 10 10"
          aria-hidden="true"
          className={`opacity-70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-1/2 top-full z-50 mt-1 min-w-[200px] -translate-x-1/2 rounded-md border border-border bg-surface-2 py-2 shadow-lg backdrop-blur-xl"
        >
          {item.children!.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformNav() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [showPlatformNav, setShowPlatformNav] = useState(false);

  // Determine if we're on a platform/admin page
  const isPlatformPage = PLATFORM_NAV.some(link => pathname?.startsWith(link.href || ''));

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 h-auto border-b border-border"
        style={{ background: 'rgba(10,15,30,0.95)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
      >
        {/* Top row: Logo + Travel Nav */}
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
          {/* Logo */}
          <Link href="/" className="flex shrink-0 items-center gap-3 mr-4">
            <AppLogo size={32} />
            <span className="flex flex-col leading-tight whitespace-nowrap">
              <span className="font-serif text-[0.85rem] tracking-[0.24em] text-primary">WORLDWAY</span>
              <span className="text-[0.5rem] uppercase tracking-[0.44em] text-muted-foreground">Luxe</span>
            </span>
          </Link>

          {/* Desktop Travel Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5 flex-1">
            {TRAVEL_NAV.map((item) => {
              if (item.children) {
                return <TravelDropdown key={item.label} item={item} />;
              }
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.label}
                  href={item.href!}
                  className={`whitespace-nowrap px-3 py-2 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-200 ${
                    isActive ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="ml-auto flex items-center gap-2">
            {/* Platform toggle */}
            <button
              onClick={() => setShowPlatformNav(!showPlatformNav)}
              className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                showPlatformNav || isPlatformPage
                  ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-surface-4 border border-border'
              }`}
            >
              <Icon name="Squares2X2Icon" size={14} />
              <span className="hidden lg:block">Platform</span>
            </button>

            {/* Sign in */}
            <Link
              href="/auth/login"
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-surface-4 transition-colors border border-border"
            >
              <Icon name="UserCircleIcon" size={14} />
              <span className="hidden lg:block">Sign in</span>
            </Link>

            {/* Mobile hamburger */}
            <button
              className="xl:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-4 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
            </button>
          </div>
        </div>

        {/* Platform Nav Row (shown when toggled or on platform pages) */}
        {(showPlatformNav || isPlatformPage) && (
          <div className="border-t border-border/50 bg-surface-2/80">
            <div className="mx-auto flex max-w-7xl items-center gap-1 px-6 py-2 overflow-x-auto">
              {PLATFORM_NAV.map((link) => {
                const isActive = pathname === link.href || (link.href !== '/' && pathname?.startsWith(link.href || ''));
                return (
                  <Link
                    key={`platform-${link.href}`}
                    href={link.href!}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-150 ${
                      isActive
                        ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-surface-4'
                    }`}
                  >
                    <Icon name={link.icon as Parameters<typeof Icon>[0]['name']} size={14} variant={isActive ? 'solid' : 'outline'} />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 xl:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute top-[57px] left-0 right-0 bg-surface-2 border-b border-border max-h-[calc(100vh-57px)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col py-2">
              {/* Travel Nav */}
              <div className="px-4 py-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Travel</p>
              </div>
              {TRAVEL_NAV.map((item) => {
                if (item.children) {
                  const isExpanded = mobileExpanded === item.label;
                  return (
                    <div key={item.label}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground hover:text-primary hover:bg-surface-4 transition-colors"
                        onClick={() => setMobileExpanded(isExpanded ? null : item.label)}
                      >
                        {item.label}
                        <svg
                          width="7" height="7" viewBox="0 0 10 10" aria-hidden="true"
                          className={`opacity-70 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        >
                          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                      </button>
                      {isExpanded && (
                        <div className="bg-surface-1 border-t border-border/50">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              href={child.href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-10 py-2.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    href={item.href!}
                    onClick={() => setMobileOpen(false)}
                    className={`px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors hover:bg-surface-4 ${
                      pathname === item.href ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {/* Platform Nav */}
              <div className="border-t border-border/50 mt-2 pt-2">
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Platform</p>
                </div>
                {PLATFORM_NAV.map((link) => {
                  const isActive = pathname === link.href;
                  return (
                    <Link
                      key={`mobile-platform-${link.href}`}
                      href={link.href!}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-6 py-3 text-sm font-medium transition-all ${
                        isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-surface-4'
                      }`}
                    >
                      <Icon name={link.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                      {link.label}
                    </Link>
                  );
                })}
              </div>

              <div className="px-6 py-4 border-t border-border/50 mt-2">
                <Link
                  href="/auth/login"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full hover:border-primary/50 hover:text-primary transition-all"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}